// Shared parser utilities for AST node and shorthand var-call handling.
// Example: isStandaloneObjectVarCall("var(--theme);").
import type { LuisArrayVariables, LuisNode, LuisNodeType, LuisObjectVariables } from '../types'
import { expandDeclarationWithObjectVariables } from '../variables/object-variable'
import { failClosedSecurityCheck } from '../../security/guard'
import { hasUnsafeCssPayload } from '@lentystyle/guard/shared'

// Expand a raw declaration into one or many declarations before storing.
export function pushDeclarations(
  node: LuisNode,
  rawDeclaration: string,
  objectVariables: LuisObjectVariables,
  arrayVariables: LuisArrayVariables = {},
): void {
  // Skip potentially dangerous declaration payloads early.
  if (containsUnsafeCssPayload(rawDeclaration)) {
    return
  } else {
    // Continue declaration expansion for safe declaration text.
  }

  const expanded = expandDeclarationWithObjectVariables(rawDeclaration, objectVariables, arrayVariables)
  // Preserve expanded declaration order after object variable expansion.
  for (const declaration of expanded) {
    // Skip dangerous expanded declarations from object substitutions.
    if (containsUnsafeCssPayload(declaration)) {
      continue
    } else {
      // Keep safe expanded declaration in AST output.
    }
    node.declarations.push(declaration)
  }
}

// Create a new AST node with empty declaration/child arrays.
export function createNode(type: LuisNodeType, value: string, line = 1): LuisNode {
  // Return new AST node initialized with empty collections.
  return {
    type,
    value,
    line,
    declarations: [],
    children: [],
  }
}

// Read the current active node on top of the parser stack.
export function currentNode(stack: LuisNode[]): LuisNode {
  // Return last parser stack item as active node.
  return stack[stack.length - 1]
}

// Detect exact `var(--name)` shorthand rows without regex matching.
export function isStandaloneObjectVarCall(input: string): boolean {
  // Reject lines that do not start with `var(`.
  if (!input.startsWith('var(')) {
    return false
  } else {
    // Continue shorthand var-call token scan.
  }

  let cursor = 4
  cursor = skipWhitespace(input, cursor)
  // Reject var calls without custom-property prefix.
  if (input[cursor] !== '-' || input[cursor + 1] !== '-') {
    return false
  } else {
    // Continue custom-property name scanning.
  }

  cursor += 2
  // Reject names with invalid starting character.
  if (!isIdentifierStart(input[cursor] ?? '')) {
    return false
  } else {
    // Continue identifier body scanning.
  }

  cursor += 1
  // Consume custom-property name characters.
  while (cursor < input.length && isIdentifierChar(input[cursor])) {
    cursor += 1
  }

  cursor = skipWhitespace(input, cursor)
  // Reject malformed calls without closing `)`.
  if (input[cursor] !== ')') {
    return false
  } else {
    // Continue trailing whitespace scan.
  }

  cursor += 1
  cursor = skipWhitespace(input, cursor)
  // Return true only when no trailing token remains.
  return cursor === input.length
}

// Skip whitespace and return first non-space index.
function skipWhitespace(input: string, fromIndex: number): number {
  let index = fromIndex
  // Advance cursor while current character is whitespace.
  while (index < input.length && isWhitespace(input[index])) {
    index += 1
  }

  // Return first non-whitespace index.
  return index
}

// Detect valid identifier start characters for custom property names.
function isIdentifierStart(char: string): boolean {
  // Return true for valid identifier start chars.
  return (
    (char >= 'a' && char <= 'z')
    || (char >= 'A' && char <= 'Z')
    || char === '_'
  )
}

// Detect valid identifier continuation characters for custom property names.
function isIdentifierChar(char: string): boolean {
  // Return true for valid identifier continuation chars.
  return isIdentifierStart(char) || (char >= '0' && char <= '9') || char === '-' || char === '_'
}

// Detect whitespace used in parser token scanning.
function isWhitespace(char: string): boolean {
  // Return true for scanner-recognized whitespace chars.
  return char === ' ' || char === '\t' || char === '\n' || char === '\r'
}

// Detect payloads that should never be emitted into generated css.
function containsUnsafeCssPayload(input: string): boolean {
  try {
    // Reuse shared guard predicate for emitted CSS payload safety.
    return hasUnsafeCssPayload(input)
  } catch (error) {
    // Stop compile flow when css payload sanitizer cannot execute.
    return failClosedSecurityCheck('containsUnsafeCssPayload', error)
  }
}
